<script setup lang="ts">
/**
 * @deprecated Use non-formkit-specific NetworkManagerSelect or its wrapped version NetworkManagerDropdownInput
 */
import type { FormkitCommonProps } from "@/components/ui/input/formkit/formkit.types";
import type { TSimpleCompanyDto } from "@/types/backoffice/dto/company/simple-company-dto";
import type { SiteId } from "@/types/common/ids";
import type { FormKitFrameworkContext } from "@formkit/core";
import type { PropType } from "vue";

import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";

import BackofficeApi from "@/api/backoffice/backoffice-api.ts";
import { CompanyQueryKeys } from "@/api/backoffice/company/company-query-keys.ts";
import { DATA_TEST_IDS } from "@/constants/data-test-ids";
import { useFormkitComputedValue } from "@/hooks/common/use-formkit";

import Dropdown from "primevue/dropdown";
import Skeleton from "primevue/skeleton";

export interface FormKitNetworkManagerDropdownProps extends FormkitCommonProps {
  insee: string | null;
  siteId: SiteId | null;
}

const props = defineProps({
  context: {
    type: Object as PropType<FormKitFrameworkContext<TSimpleCompanyDto> & FormKitNetworkManagerDropdownProps>,
  },
});

const inseeRef = computed(() => props.context?.insee ?? undefined);
const siteIdRef = computed(() => props.context?.siteId ?? undefined);

const { data: networkManagers, isLoading: isNetworkManagersLoading } = useQuery({
  queryKey: CompanyQueryKeys.findNetworkManagers(siteIdRef, inseeRef),
  queryFn: () => {
    const siteId = siteIdRef.value;
    const insee = inseeRef.value;

    return siteId
      ? BackofficeApi.guessNetworkManagers({
          params: { siteId },
          queries: { insee },
        })
      : BackofficeApi.findNetworkManagers({ queries: { insee } });
  },
});

const model = useFormkitComputedValue<TSimpleCompanyDto>(props.context);

const isDisabled = computed(
  () => props.context?.disabled || networkManagers.value === undefined || networkManagers.value.length < 2,
);
</script>

<template>
  <Skeleton
    v-if="isNetworkManagersLoading"
    class="w-full h-full"
    style="height: 3rem"
    :data-test-id="DATA_TEST_IDS.components.networkManagerDropdown.skeleton()"
  />
  <Dropdown
    v-model="model"
    filter
    :loading="isNetworkManagersLoading"
    :options="networkManagers"
    :name="props.context?.name"
    :disabled="isDisabled"
    option-label="name"
    data-key="id"
    :placeholder="props.context?.placeholder"
    class="w-full"
    v-bind="$attrs"
    :data-test-id="DATA_TEST_IDS.components.networkManagerDropdown.dropdown()"
  />
</template>

<style scoped></style>
